/**
 * Operator page: generate a paid-traffic landing page and resolve what the
 * gate refuses.
 *
 * This is a REVIEW screen, not a builder. The generation path is
 * deterministic and already works; what has been missing is anywhere for an
 * operator to answer the questions the gate asks. Nine clients in the last
 * batch produced five refusals and gates, and every one needed a human
 * decision that had nowhere to happen:
 *
 *   "three forms found — which is wired to your lead routing?"
 *   "no photo carries `residential` — pick one or upload"
 *   "no phone. provision one, or say why this page ships without"
 *
 * Deliberately NOT a template designer or a copy editor. The architecture's
 * premise is that the deterministic path is the product and the operator's
 * job is sourcing decisions, not layout — a rich editor would invite the
 * hand-tuning that makes forty clients unscalable.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  AlertTriangle, Ban, Download, ExternalLink, ImageOff, Loader2, Info, LayoutTemplate, Maximize2,
  MessageSquare, Minimize2, Monitor, Play, ShieldCheck, Smartphone,
} from 'lucide-react';

import { useMutation, useQueryClient } from '@tanstack/react-query';

import { HeroImagery } from '@/components/landing/HeroImagery';
import { TemplateSkeleton } from '@/components/landing/TemplateSkeleton';
import BuilderTweaks from '@/components/landing/BuilderTweaks';
import DraftLayout from '@/components/landing/DraftLayout';
import FavoritesLibrary from '@/components/landing/SectionFavorites/FavoritesLibrary';
import SectionPicker from '@/components/landing/SectionFavorites/SectionPicker';
import { useFrameContentHeight } from '@/components/landing/useFrameContentHeight';

import { toPreviewDocument } from '@/components/landing/previewShortcodes';
import { useTenantStore } from '@/stores/useTenantStore';
import { useWorkspaceStore } from '@/stores/useWorkspaceStore';
import { decideArrival } from './landingArrival';
import DraftHistory from '@/components/landing/DraftHistory';
import BandSwapPanel from '@/components/landing/SectionFavorites/BandSwapPanel';
import FormPicker from '@/components/landing/FormPicker';
import ArtIntake from '@/components/landing/ArtIntake';
import {
  dshLaneOn,
  landingPagesService,
  type LandingDraftFull,
  type LandingPreviewResponse,
} from '@/services/landingPagesService';
import { TooltipProvider } from '@/components/ui/tooltip';
import {
  DSH_MEDIAN_MS,
  LandingRefusal,
  useLandingDraft,
  useLandingDrafts,
  useLandingDshRun,
  useLandingPreview,
  useLandingPublish,
  useLandingAdContext,
  useLandingSuggestions,
  useLandingTemplates,
} from '../hooks/useLandingPreview';

/**
 * Findings, grouped by WHO CAN ACT — Viktor's rule, the same one already
 * adopted for the brand three-state model: bucket by consequence, not by
 * cause. A two-click fix and a week-long blocker used to look identical.
 */
type Owner = 'you' | 'someone' | 'info';

/**
 * Which findings an operator can resolve on this screen, and which need a
 * different person. The owner is the actionable half — a blocker with no
 * name is how a 0-of-9 batch becomes permanent.
 */
const ROUTING: Array<{
  re: RegExp;
  owner: Owner;
  says: string;
  fix: string;
  who?: string;
}> = [
  {
    re: /^form_.*multiple|forms found on this site/,
    owner: 'you',
    says: 'This site runs several real forms.',
    fix: 'Pick the one wired to their lead routing — the wrong one sends submissions where nobody is watching.',
  },
  {
    re: /^LP-020:hero_unrelated|^LP-020:hero_weak_match/,
    owner: 'you',
    says: 'The hero does not show the service this ad sells.',
    fix: 'Choose another photo, or tag one in the image pool for this service.',
  },
  {
    re: /^LP-020:portrait_hero/,
    owner: 'you',
    says: 'A portrait photo is being cropped to fill a landscape slot.',
    fix: 'Most of the frame is lost. Pick a landscape photo.',
  },
  {
    re: /^LC-07|^LC-09/,
    owner: 'someone',
    who: 'whoever provisions tracking numbers',
    says: 'This page can be called and renders no phone number.',
    fix: 'Add a verified number to the record — or say below why this page ships without one.',
  },
  {
    re: /^BRD-03/,
    owner: 'someone',
    who: 'whoever collects client photos',
    says: 'No usable photo exists in this client’s pool.',
    fix: 'Upload photographs of the work, labelled by service.',
  },
  {
    re: /^BRD-05/,
    owner: 'someone',
    who: 'the brand bible team',
    says: 'The brand colour on record does not appear on the client’s own site.',
    fix: 'Confirm which colour is current.',
  },
  {
    re: /^ID-07|^brand_unsourced|^no_sourced_trust_element/,
    owner: 'someone',
    who: 'the brand bible team',
    says: 'The record is missing something this page needs.',
    fix: 'Filling it fixes every future page for this client, not just this one.',
  },
  {
    re: /^unverified_claim_in_copy/,
    owner: 'you',
    says: 'The copy states something no record backs up.',
    fix: 'It is inside a sentence, so it cannot be hidden. Regenerate, or record the fact if it is true.',
  },
  {
    re: /^TRUST-03/,
    owner: 'info',
    says: 'A price-quote call to action was replaced.',
    fix: 'Implying a price before treatment is a promise this business cannot keep.',
  },
  {
    re: /^BRD-06/,
    owner: 'info',
    says: 'This page rendered in the default palette.',
    fix: 'No brand colour was sourced, so it looks like nobody’s client.',
  },
  {
    re: /^MM-09:hero_weight/,
    owner: 'someone',
    who: 'whoever collects client photos',
    says: 'The hero image is too heavy for a mobile ad click.',
    fix: 'The visitor waits on it after we already paid for the click.',
  },

  // ---------------------------------------------------------------------
  // Everything below exists so that NO finding reaches an operator as a raw
  // key. "ID-05:unsourced_partner:Synchrony" is readable to whoever wrote the
  // check and to nobody else, and a person who cannot read a finding cannot
  // act on it or judge whether it matters. {detail} interpolates whatever the
  // key carries after its last colon.
  // ---------------------------------------------------------------------
  {
    re: /^ID-05:unsourced_partner/,
    owner: 'someone',
    who: 'the brand bible team',
    says: 'The page names {detail}, and nothing on record says this client works with them.',
    fix: 'Naming a finance or certification partner we cannot evidence is a claim about someone else’s business. Confirm the relationship or it stays out.',
  },
  {
    re: /^keyword_support_thin/,
    owner: 'info',
    says: 'The page mentions the ad keyword, but only just.',
    fix: 'A visitor who clicked that ad should see it echoed in the first screen. Thin support usually means the copy drifted to generic wording.',
  },
  {
    re: /^keyword_redundant/,
    owner: 'info',
    says: 'The ad keyword repeats more than it reads naturally.',
    fix: 'Repetition past the headline reads as written-for-a-machine, not for the visitor.',
  },
  {
    re: /^MM-02:keyword_absent/,
    owner: 'you',
    says: 'The page never says the words the visitor searched for.',
    fix: 'They clicked an ad for “{detail}” and the page talks about something else. That mismatch is the single most common reason a paid click leaves.',
  },
  {
    re: /^MM-02:no_phrase_support/,
    owner: 'info',
    says: 'The keyword appears in the headline but nowhere in the body.',
    fix: 'A headline that the rest of the page does not back up reads like bait.',
  },
  {
    re: /^MM-06:(high_similarity|near_duplicate)/,
    owner: 'you',
    says: 'This page is very close to another page we built.',
    fix: 'Near-identical pages compete with each other and read as filler. Change the angle, or use one page for both ad groups.',
  },
  {
    re: /^MM-09:over_word_budget/,
    owner: 'info',
    says: 'A section came back longer than it should be.',
    fix: 'Long blocks get skipped on a phone, which is where most ad clicks are.',
  },
  {
    re: /^hype:/,
    owner: 'you',
    says: 'The copy uses salesy language: {detail}.',
    fix: 'Words like “best” or “#1” are claims we cannot evidence, and they read as noise. Leave a note on that section and regenerate.',
  },
  {
    re: /^unverified_claim:/,
    owner: 'info',
    says: 'A claim we cannot evidence was hidden rather than shipped: {detail}.',
    fix: 'The page renders without it. If it is genuinely true, recording it on the client makes it usable on every future page.',
  },
  {
    re: /^TPL-03:unlicensed_commitment/,
    owner: 'you',
    says: 'The page promises something on the client’s behalf: {detail}.',
    fix: 'A guarantee or commitment we cannot evidence is a promise the business has to honour. Regenerate that section.',
  },
  {
    re: /^TPL-05:.*quote_price_promise/,
    owner: 'info',
    says: 'A price-quote promise was removed for this industry.',
    fix: 'Implying a price before the work is understood is not a promise this kind of business can keep.',
  },
  {
    re: /^TPL-05:/,
    owner: 'info',
    says: 'A section was left out because it does not belong on this kind of page.',
    fix: 'The template carries sections that suit some industries and not others.',
  },
  {
    re: /^CTA-01:too_long/,
    owner: 'you',
    says: 'The button text is too long ({detail}).',
    fix: 'It wraps or truncates on a phone. Short and specific beats complete.',
  },
  {
    re: /^CTA-01:(parenthetical_phone|punctuation)/,
    owner: 'you',
    says: 'The button text has punctuation that does not belong on a button.',
    fix: 'Buttons read as labels, not sentences.',
  },
  {
    re: /^CTA-02:meta_prose/,
    owner: 'you',
    says: 'The description reads like it is describing the page rather than talking to the visitor.',
    fix: 'Write it as the sentence a searcher sees under the link.',
  },
  {
    re: /^BRD-04:not_public_url/,
    owner: 'someone',
    who: 'whoever manages the image library',
    says: 'A photo was found but its link is not publicly reachable.',
    fix: 'It would render as a broken image on the live site, so it was left out.',
  },
  {
    re: /^(form_shortcode_missing|clickid_capture_missing)/,
    owner: 'someone',
    who: 'whoever set up this client’s forms',
    says: 'The lead form is not correctly wired on this page.',
    fix: 'A form that looks fine and posts nowhere loses every lead silently. This is the single most expensive failure we see.',
  },
  {
    re: /^(phone_expected_but_no_tel_link|phone_in_button_without_tel|tel_link_with_no_verified_phone|LC-08)/,
    owner: 'someone',
    who: 'whoever provisions tracking numbers',
    says: 'The phone number on the page cannot be tapped to call.',
    fix: 'On a phone, a number that is not a link is a number nobody rings.',
  },
  {
    re: /^phone_unsourced_operator_gate/,
    owner: 'you',
    says: 'A phone number was supplied by hand rather than from the client record.',
    fix: 'It works for this page only. Recording it on the client fixes every future page.',
  },
  {
    re: /^h1_count/,
    owner: 'you',
    says: 'The page has the wrong number of main headings ({detail}).',
    fix: 'A page should have exactly one. More than one confuses both readers and search engines.',
  },
  {
    re: /^h1_missing_keyword_words/,
    owner: 'you',
    says: 'The main heading is missing words from the ad keyword: {detail}.',
    fix: 'The headline is the first thing a visitor checks against the ad they clicked.',
  },
  {
    re: /^(leftover_markers|leftover_placeholders)/,
    owner: 'you',
    says: 'The page still contains build placeholders that a visitor would see.',
    fix: 'Regenerate. This should never reach a live page.',
  },
  {
    re: /^(page_heavy|page_too_heavy)/,
    owner: 'info',
    says: 'The page is heavy to load ({detail}).',
    fix: 'We have already paid for the click by the time it loads.',
  },
  {
    re: /^noindex_missing/,
    owner: 'you',
    says: 'This draft is missing the tag that keeps it out of search results.',
    fix: 'A landing page for paid traffic should not compete with the client’s own pages.',
  },
  {
    re: /^SHELL:/,
    owner: 'info',
    says: 'The page frame did not assemble as expected.',
    fix: 'Usually a template problem rather than a client-record one. Worth reporting if it repeats.',
  },
];

/** Whatever a finding key carries after its last colon, for {detail}. */
const detailOf = (key: string) => {
  const i = key.lastIndexOf(':');
  return i > 0 ? key.slice(i + 1).trim() : '';
};

// An unrouted finding has no human sentence to show, so it used to fall back
// to the raw key — which the <code> line then printed again directly
// underneath. Two identical lines reads as a rendering bug and wastes the row.
// `says: null` means "no sentence yet"; the row shows the key once.
const route = (finding: string) => {
  const hit = ROUTING.find((r) => r.re.test(finding));
  if (!hit) {
    return { owner: 'info' as Owner, says: null, fix: '', who: undefined };
  }
  const detail = detailOf(finding);
  const fill = (s: string) => s.replace(/\{detail\}/g, detail || 'this');
  return { ...hit, says: fill(hit.says), fix: fill(hit.fix) };
};

/** Section names in operator language, matching the shape preview's labels. */
const SECTION_LABEL: Record<string, string> = {
  hero: 'Hero and headline',
  form: 'Form',
  benefits: 'Benefits',
  steps: 'How it works',
  proof: 'Proof',
  offer: 'Offer',
  faq: 'FAQ',
  final: 'Closing CTA',
};

const SECTION_PLACEHOLDER: Record<string, string> = {
  hero: 'e.g. lead with the free estimate, not the years in business',
  form: 'e.g. the intro line above the form is too long',
  benefits: 'e.g. say material choice, not "quality workmanship"',
  steps: 'e.g. step 2 should mention the site visit is free',
  proof: 'e.g. drop the review count, it reads as thin',
  offer: 'e.g. no offer exists, keep this factual',
  faq: 'e.g. add a question about how long installation takes',
  final: 'e.g. repeat the phone number here',
};

const GROUPS: Array<{ owner: Owner; label: string; tone: string }> = [
  { owner: 'you', label: 'You can fix these now', tone: 'text-coral' },
  { owner: 'someone', label: 'Waiting on someone else', tone: 'text-teal-dark' },
  { owner: 'info', label: 'Worth knowing', tone: 'text-teal' },
];



export default function LandingPagesPage() {
  const currentTenant = useTenantStore((s) => s.currentTenant);
  const tenantId = currentTenant ?? '';

  // ⚠ DECLARED BEFORE useLandingAdContext READS THEM, WHICH IS THE WHOLE FIX.
  // These sat ~20 lines below the call that passes them, so every render hit
  // the temporal dead zone: `ReferenceError: Cannot access 'campaignRn'
  // before initialization`. Minification renamed it to 'x', so the screen
  // said "Cannot access 'x' before initialization" and named nothing useful.
  //
  // It crashed the ENTIRE route — the operator screen for landing pages has
  // never rendered in a production build. It survived because `vite build`
  // does not evaluate components, the module imports fine on its own, and
  // nobody opens this screen: landing is dev-only.
  //
  // Campaign and ad group the operator picked, if this client has ads at all.
  // ⚠ NO AD GROUP STATE. There used to be one, and picking it was the
  // problem: campaign, ad group, service and vertical were four inputs that
  // could contradict each other and three were implied by the first. The
  // campaign is the unit now; the backend still accepts an ad group to narrow
  // with, and nothing in this screen sends one.
  const [campaignRn, setCampaignRn] = useState('');

  const { data: templates } = useLandingTemplates();
  const { data: suggest } = useLandingSuggestions(tenantId || null);
  const { data: adCtx, isLoading: adCtxLoading } = useLandingAdContext(
    tenantId || null,
    campaignRn || null,
    null,
  );
  const preview = useLandingPreview();
  const publish = useLandingPublish();
  const dshRun = useLandingDshRun(tenantId);
  const generating = preview.isPending || dshRun.running;
  const elapsedLabel = `${Math.floor(dshRun.elapsedMs / 60_000)}m ${String(
    Math.floor((dshRun.elapsedMs % 60_000) / 1000),
  ).padStart(2, '0')}s`;
  const queryClient = useQueryClient();

  // The WordPress result is nested under `published`, and the WP service uses
  // post_id/url in some paths and id/link in others — read both rather than
  // rendering "post undefined" at the one moment an operator needs the link.
  const published = publish.data?.published ?? null;
  const publishedId = published?.post_id ?? published?.id ?? null;
  const publishedUrl = published?.url ?? published?.link ?? null;
  const publishError = publish.error
    ? publish.error instanceof Error
      ? publish.error.message
      : String(publish.error)
    : null;

  const [serviceName, setServiceName] = useState('');
  const [keyword, setKeyword] = useState('');
  const [vertical, setVertical] = useState('');
  // ⚠ Declared here, well above the JSX that reads them. The whole route was
  // down for eight days because two useState calls sat BELOW the hook that
  // used them (#1718); adding state to this file now means checking that.
  // Tri-state ON PURPOSE: '' unstated, 'yes' / 'no' stated. Not a boolean,
  // because "nobody said" and "somebody said yes" are different facts and the
  // gate treats them differently — see the control below.
  const [photographable, setPhotographable] = useState<'' | 'yes' | 'no'>('');
  const [serviceOther, setServiceOther] = useState(false);
  // ⚠ NEVER EMPTY ON THE WIRE. `template_slug` is required by the API (an
  // empty one is a 404, not a 422), the engine reads it and the gate enforces
  // it. The slot carries the operator's pick, else Form First, else the first
  // row the server lists. A pick the server no longer lists — a reopened run
  // that stored a retired slug — is not a pick: it would leave the slot blank
  // and put a 404 on the wire.
  const [templatePick, setTemplatePick] = useState('');
  const listed = (slug: string) => (templates ?? []).some((t) => t.slug === slug);
  const templateSlug =
    (listed(templatePick) ? templatePick : '') ||
    (listed('landing-form-first') ? 'landing-form-first' : '') ||
    templates?.[0]?.slug ||
    '';
  const [noPhoneReason, setNoPhoneReason] = useState('');
  // The "change" row under the sentence. Closed by default, never persisted.
  const [changeOpen, setChangeOpen] = useState(false);
  // Which side the right pane is showing. Flips to the built page the moment
  // one exists, and stays reachable both ways afterwards — comparing the
  // shape you picked against the page you got is the useful comparison.
  const [pane, setPane] = useState<'shape' | 'page'>('shape');
  // Which stored run is being viewed, if any. null = looking at the run just
  // generated (or nothing yet).
  /* ⚠ THE OPEN RUN LIVES IN THE URL, NOT ONLY IN STATE.
   *
   * Review of a generated page happens with somebody who is not sitting at
   * this screen: comms, the client's account lead, whoever asked for it. Held
   * in component state alone, the only way to show them anything was "open the
   * tool, pick the tenant, go to History, find the run from Tuesday" — four
   * steps in which the wrong run gets opened and reviewed.
   *
   * The tenant rides along too. A link that opens the right run under the
   * WRONG client is worse than a link that fails, because it renders happily
   * and quietly shows somebody another client's page. */
  const [searchParams, setSearchParams] = useSearchParams();
  const [openDraftId, setOpenDraftId] = useState<string | null>(
    searchParams.get('draft'),
  );
  // ⚠ HISTORY IS ITS OWN TAB, NOT A PANEL UNDER THE FORM (Jacob, 2026-08-19).
  // Under the form it competed with the controls for a 26rem column and read
  // as an afterthought to one generation; as a tab it is the section's record,
  // gets the full width its campaign -> day -> run nesting needs, and is where
  // Location and Service pages will appear when they ship.
  const [tab, setTab] = useState<
    'builder' | 'history' | 'form' | 'facts' | 'favorites'
  >('builder');
  /* Per-run adjustments. Both reset when the template changes: a section
     order is a permutation of THAT template's sections, so carrying one across
     a template switch sends a list the server has to refuse. */
  const [overrides, setOverrides] = useState<Record<string, string>>({});
  const [sectionOrder, setSectionOrder] = useState<string[]>([]);
  const [expanded, setExpanded] = useState(false);
  const [layoutHtml, setLayoutHtml] = useState<string | null>(null);
  useEffect(() => setLayoutHtml(null), [openDraftId]);
  const [showKeys, setShowKeys] = useState(false);
  // ⚠ DESKTOP IS THE DEFAULT, AND THAT IS A REVIEW DECISION, NOT A TRAFFIC ONE.
  // Phone was the default because a paid ad click usually is one, which is true
  // and was the wrong reason: this pane is where a person REVIEWS the page, and
  // every desktop-only rule in a treatment is invisible at 390px. Photo
  // Overlay's floating form card only exists above 960px, so an operator picked
  // a template FOR that card and then reviewed a page where it had collapsed —
  // the argument the old comment made for keeping the control is the argument
  // for which end it should start at. Phone is one click away and still the
  // right last look before publishing.
  const [width, setWidth] = useState<'phone' | 'desktop'>('desktop');

  // Revision notes, per section. Kept in localStorage against this exact
  // client + service + template: a reload used to lose everything you had
  // just written while reading a page, and re-reading a page to re-derive
  // notes is the slowest possible way to lose work.
  const fbKey = `landing:fb:${tenantId}:${serviceName.trim().toLowerCase()}:${templateSlug}`;
  const [feedback, setFeedback] = useState<Record<string, string>>({});
  useEffect(() => {
    try {
      setFeedback(JSON.parse(localStorage.getItem(fbKey) || '{}'));
    } catch {
      setFeedback({});
    }
  }, [fbKey]);
  const noteFor = (section: string, text: string) => {
    const next = { ...feedback, [section]: text };
    setFeedback(next);
    try {
      localStorage.setItem(fbKey, JSON.stringify(next));
    } catch {
      /* private mode or quota — the notes still work for this session */
    }
  };
  const noteCount = Object.values(feedback).filter((v) => v.trim()).length;

  const selected = useMemo(
    () => (templates ?? []).find((t) => t.slug === templateSlug) ?? null,
    [templates, templateSlug]
  );
  // I'm Feeling Lucky has a row and a treatment shape like the others, but the
  // shape is a placeholder: the engine ideates three directions and builds one.
  // A skeleton drawn from it would promise a layout nobody has chosen.
  const engineChoosesLayout = !!selected && /feeling-lucky$/.test(selected.slug);

  // Fill from the client's own record the moment it lands. The guard is "has
  // a value", not "has anyone interacted": a `touched` flag once dead-ended
  // the form (a campaign whose ad groups derived no service left Service
  // empty and Build greyed out with nothing saying why). Nothing is ever
  // overwritten, and a field nobody filled still gets filled.
  useEffect(() => {
    if (!suggest) return;
    if (!serviceName.trim() && suggest.services[0]) setServiceName(suggest.services[0]);
    if (!keyword.trim() && suggest.suggested_keyword) setKeyword(suggest.suggested_keyword);
    if (!vertical.trim() && suggest.vertical) setVertical(suggest.vertical);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [suggest]);

  // A campaign belongs to one client. Carrying a selection across a tenant
  // switch would build a page for one client from another's ad group — the
  // cross-tenant bleed the gate exists to catch, introduced by the UI.
  //
  // ⚠ THE SAME BLEED WAS HAPPENING TO EVERY OTHER CLIENT-DERIVED FIELD, JUST
  // QUIETER. Only campaign and its keywords were reset here. Switching from a
  // client with a service selected to one with no services on record left
  // the <select> rendering blank — the exact "state holds an off-roster value,
  // the control can't display it" trap documented below at the draft-reopen
  // path — while `serviceName` itself stayed truthy with the PREVIOUS
  // client's service. `canGenerate` only checks truthiness, so the button
  // looked normal and a generate would have built a page under the wrong
  // client's service name, silently. keyword/vertical/photographable/
  // noPhoneReason/overrides/feedback are the same shape: typed or derived
  // for one client's record, not scoped or cleared when the client changes.
  //
  // ⚠ CLEARING THE FORM AND LEAVING THE RESULT ON SCREEN IS THE SAME BUG ONE
  // LAYER UP (Viktor, reviewing the fix that moved the refusal/failure panel
  // directly under the button). `preview` still held the PREVIOUS client's
  // page/error, `openDraftId` could still point at their stored run, and
  // `pane` could still be showing it — so switching clients now surfaced a
  // stale result as if it belonged to whoever is currently selected, exactly
  // where an operator would first look. `templateSlug` resets too: leaving it
  // set skipped the `[templateSlug]`-keyed effect below that clears
  // `sectionOrder`, so a reorder customized for one client's template could
  // also ride along into another's run silently.
  useEffect(() => {
    setCampaignRn('');
    setServiceName('');
    setServiceOther(false);
    setKeyword('');
    setVertical('');
    setPhotographable('');
    setNoPhoneReason('');
    setOverrides({});
    setFeedback({});
    setTemplatePick('');
    preview.reset();
    dshRun.cancel();
    setOpenDraftId(null);
    setPane('shape');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenantId]);

  /* ⚠ A SECTION ORDER BELONGS TO ONE TEMPLATE. It is a permutation of THAT
     template's sections, so carrying it across a template switch sends the
     server a list it has to refuse — the operator would see a 422 for a
     choice they did not knowingly make. Same reasoning as the campaign reset
     above: a selection scoped to one thing must not survive a change of that
     thing. */
  useEffect(() => {
    setSectionOrder([]);
  }, [templateSlug]);

  // FILLS THE FIELDS, NEVER LOCKS THEM. The derived name is better than a
  // typed one because an ad group is already the singular, client-approved
  // concept — but an operator who disagrees must be able to overwrite it, and
  // 28 of 53 clients have no ads to derive from at all.
  useEffect(() => {
    if (!adCtx?.available || !campaignRn) return;
    // The campaign's own answer beats the dossier's, because an ad group is
    // the singular client-approved concept. When it has no answer, the field
    // must not be left empty — see the dead-end described above.
    if (adCtx.service_name) setServiceName(adCtx.service_name);
    else if (!serviceName.trim() && suggest?.services?.[0]) {
      setServiceName(suggest.services[0]);
    }
    // ⚠ ONLY WHEN THE CAMPAIGN AGREED ON A SERVICE. With several candidates
    // the backend's pooled fallback is the shortest of every keyword in the
    // campaign — `fargo` on a real account — and writing that into the field
    // means the operator either ships it or has to notice and undo it. Each
    // candidate carries its own H1, applied when one is picked.
    if (adCtx.service_name && adCtx.target_keyword) setKeyword(adCtx.target_keyword);
    // Vertical is derived the same way and fills the same way. Blank when the
    // campaign's words matched nothing or two verticals tied — both are
    // honest, and neither is a reason to overwrite what the dossier suggested.
    if (adCtx.vertical) setVertical(adCtx.vertical);
  }, [
    adCtx?.service_name,
    adCtx?.target_keyword,
    adCtx?.vertical,
    adCtx?.available,
    campaignRn,
  ]);

  const history = useLandingDrafts(tenantId);
  const openedDraft = useLandingDraft(tenantId, openDraftId);

  /* The tenant the link was written for, which may not be the one selected. */
  const linkTenant = searchParams.get('tenant');
  const workspaces = useWorkspaceStore((s) => s.workspaces);
  const workspacesLoading = useWorkspaceStore((s) => s.isLoading);
  const selectTenantByWebsite = useWorkspaceStore((s) => s.selectTenantByWebsite);
  const linkTenantIsMine = useMemo(() => {
    const wanted = (linkTenant ?? '').trim().toLowerCase();
    return !!wanted && workspaces.some(
      (w) => (w.website ?? '').trim().toLowerCase() === wanted,
    );
  }, [workspaces, linkTenant]);
  const arrival = decideArrival({
    draftId: openDraftId,
    linkTenant,
    tenantId,
    workspacesLoaded: !workspacesLoading && workspaces.length > 0,
    linkTenantIsMine,
  });
  const tenantMismatch = arrival === 'no-access';

  /* ⚠ ARRIVING BY LINK MUST LAND ON THE PAGE, NOT ON THE FORM. Restoring the
   * id alone left the reviewer on the builder with the inputs filled and no
   * rendering, which reads as "the link is broken" rather than "press the tab
   * you cannot see". Runs once: after that the operator's own clicks decide
   * what they are looking at. */
  const [arrived, setArrived] = useState(false);
  const [copied, setCopied] = useState(false);
  useEffect(() => {
    if (arrived || arrival === 'wait') return;
    if (arrival === 'switch') {
      // Deliberately does NOT mark arrival: selecting the client re-runs this
      // with 'open', which is the pass that actually shows the page.
      selectTenantByWebsite(linkTenant as string);
      return;
    }
    setArrived(true);
    if (arrival === 'open') {
      setPane('page');
      setTab('builder');
    }
  }, [arrived, arrival, linkTenant, selectTenantByWebsite]);

  // ⚠ ONE SOURCE OF TRUTH FOR WHAT IS ON SCREEN. A reopened run and a fresh
  // generation are both "the page you are looking at", and every consumer —
  // the iframe, the findings list, the form-provider readout — has to agree
  // about which. Leaving the findings pointed at preview.data while the frame
  // showed a stored page would report one run's checks beside another run's
  // HTML: the reporter-outlives-the-condition defect, on screen, silently.
  const shown = openDraftId ? (openedDraft.data ?? null) : (preview.data ?? null);
  const viewingSaved = !!openDraftId && !!openedDraft.data;

  const qa = shown?.qa as LandingPreviewResponse['qa'] | undefined;

  const publishedDraftId =
    (shown as LandingPreviewResponse | null)?.draft_id ??
    (shown as LandingDraftFull | null)?.id ??
    undefined;
  const shownArtifactId = (shown?.copy_meta as { artifact_id?: unknown } | undefined)
    ?.artifact_id;
  const publishedArtifactId =
    typeof shownArtifactId === 'string' && shownArtifactId ? shownArtifactId : undefined;

  // Names an operator recognises, from the builder's own `defaulted` list.
  // Per-request trims (a capped CTA, a stripped claim) are the builder editing
  // its own output, not a gap in the client's record, so they stay out.
  const brandGaps = ((shown?.defaulted as string[] | undefined) ?? [])
    .filter(
      (d) =>
        d !== 'cta_text_capped' && d !== 'value_proposition_claims_stripped',
    )
    .map((d) => {
      const [head, tail] = d.split(':', 2);
      if (head === 'trust_facts') {
        return `trust facts (${(tail || '').replace('_of_', ' of ')})`;
      }
      if (head === 'logo') return 'logo';
      if (head === 'proof') return 'ratings';
      return head.replace(/_/g, ' ');
    });
  const refusal =
    (preview.error instanceof LandingRefusal ? preview.error.detail : null) ??
    dshRun.error;
  // Anything that is NOT a refusal — a 500, a dead upstream key, a dropped
  // connection. Without this branch the mutation settles, the button resets,
  // and the screen shows exactly what it showed before the click. An operator
  // cannot tell "it failed" from "I missed the button", which is the same
  // silent-success failure we keep finding everywhere else.
  const failure =
    preview.error && !(preview.error instanceof LandingRefusal)
      ? preview.error instanceof Error
        ? preview.error.message
        : String(preview.error)
      : null;

  // Moved next to the button on purpose (see the comment at the render site),
  // but a small window or a long "Adjust this run" panel from a PRIOR run
  // still sitting above it can put this just past the fold. Scroll it into
  // view rather than trust position alone — the button settling and the
  // operator SEEING why are the same event either way.
  //
  // ⚠ KEYED ON THE ATTEMPT, NOT THE MESSAGE (Viktor). `[refusal, failure]`
  // compares the rendered strings, so clicking Generate, getting refused,
  // changing nothing, and clicking again produces an IDENTICAL refusal
  // string — same dependency value, effect doesn't re-fire, no scroll. That
  // is John's "does nothing" symptom again, on the retry path specifically.
  // `submittedAt` changes on every `.mutate()` call regardless of the
  // outcome, so a repeat of the same refusal still scrolls to it.
  const resultRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!refusal && !failure) return;
    resultRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [preview.submittedAt, refusal, failure]);

  useEffect(() => {
    if (!dshRun.readyDraftId) return;
    openDraft(dshRun.readyDraftId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dshRun.readyDraftId]);

  // A built page is what the operator asked for, so show it without being
  // asked. The shape stays one click away rather than being replaced.
  useEffect(() => {
    if (!preview.data?.html) return;
    setPane('page');
    // A new run is now the thing on screen; stop showing the stored one.
    setOpenDraftId(null);
    // And it belongs in the list without a manual reload.
    queryClient.invalidateQueries({ queryKey: ['landing-drafts', tenantId] });
  }, [preview.data?.html, queryClient, tenantId]);

  // The campaign's services and the client's own, as one list the Service
  // control can render and one predicate for "is the current value in it".
  // `knownService` decides whether the <select> can display the value at all:
  // a value it has no option for renders blank, which is how the operator ends
  // up staring at an empty required field that already has a value.
  const candidates = adCtx?.available ? (adCtx.service_name_candidates ?? []) : [];
  // ⚠ ONE PREDICATE, TWO CALLERS. The render guard below asks "can the select
  // display the CURRENT value"; reopening a stored draft has to ask the same
  // question about a value it is ABOUT to set. Two copies would drift, and the
  // drift is invisible: the field just renders empty.
  const serviceIsKnown = (name: string) =>
    candidates.some((c) => c.name === name) ||
    (suggest?.services ?? []).includes(name);
  const knownService = serviceIsKnown(serviceName);

  const canGenerate = Boolean(tenantId && serviceName.trim() && templateSlug);

  // Every finding, routed to whoever can act on it, blocking flagged
  // separately from shipped-anyway — those are different events, and
  // conflating them is how "Free Quote" reached a dental page three times.
  const routed = useMemo(() => {
    if (!qa) return [];
    const seen = new Set<string>();
    const all: Array<{ key: string; blocking: boolean }> = [
      ...(qa.fatal ?? []).map((k) => ({ key: k, blocking: true })),
      ...(qa.data_gaps ?? []).map((k) => ({ key: k, blocking: false })),
      ...(qa.suppressible ?? []).map((k) => ({ key: k, blocking: false })),
      ...(qa.warns ?? []).map((k) => ({ key: k, blocking: false })),
    ];
    return all
      .filter((f) => !seen.has(f.key) && seen.add(f.key))
      .map((f) => ({ ...f, ...route(f.key) }));
  }, [qa]);

  const counts = useMemo(
    () => ({
      you: routed.filter((f) => f.owner === 'you').length,
      someone: routed.filter((f) => f.owner === 'someone').length,
      info: routed.filter((f) => f.owner === 'info').length,
    }),
    [routed]
  );

  /** Open a stored run: show its page and put the form back the way it was.
   *
   *  ⚠ RESTORING THE INPUTS IS THE POINT, not a nicety. A draft you can look
   *  at but not continue from is a screenshot. Reopening means "regenerate
   *  this with one thing changed", which is the whole reason to keep runs. */
  const openDraft = (id: string) => {
    setOpenDraftId(id);
    // replace, not push: reopening runs from the list should not fill the back
    // button with every run the operator glanced at.
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.set('draft', id);
        next.set('tenant', tenantId);
        return next;
      },
      { replace: true },
    );
    setPane('page');
    // Back to the builder: the point of reopening is to continue from a run,
    // and the form it restores lives there. Leaving the operator on the list
    // after rewriting the form behind them would hide the effect of their own
    // click.
    setTab('builder');
    landingPagesService
      .getDraft(tenantId, id)
      .then((d) => {
        // Tri-state: null is UNSTATED and is not the same as "no". Collapsing
        // it to a boolean would state a fact about the client's service that
        // nobody made.
        //
        // ⚠ FIRST IN THIS CALLBACK. A source-scanning test asserts the
        // expression appears within the first 2000 characters of openDraft;
        // anything above this (searchParams, comments) already spends most of
        // that window.
        setPhotographable(
          d.service_photographable == null ? '' : d.service_photographable ? 'yes' : 'no'
        );
        // ⚠ setServiceName ALONE SILENTLY DROPS AN OFF-ROSTER SERVICE. The
        // <select> can only display a value it has an <option> for, so a
        // hand-typed service — the case this whole history exists to serve,
        // since "Online Marketing Agency" is not one of agileandco's seven
        // roster services — restored into a control that renders blank. The
        // state held the value and the screen showed an empty required field.
        // Found by reopening a real draft and reading the DOM, not the state.
        setServiceOther(!serviceIsKnown(d.service_name));
        setServiceName(d.service_name);
        setKeyword(d.target_keyword || '');
        setTemplatePick(d.template_slug);
        setVertical(d.vertical || '');
        setCampaignRn(d.campaign_resource_name || '');
        setFeedback(d.section_feedback || {});
        // Tri-state: null is UNSTATED and is not the same as "no". Collapsing
        // it to a boolean would state a fact about the client's service that
        // nobody made.
        setPhotographable(
          d.service_photographable == null ? '' : d.service_photographable ? 'yes' : 'no'
        );
      })
      .catch(() => {
        /* the query below surfaces the failure; this only fills the form */
      });
  };

  const run = () => {
    const req = {
      tenant_id: tenantId,
      template_slug: templateSlug,
      service_name: serviceName.trim(),
      target_keyword: keyword.trim() || undefined,
      service_photographable:
        photographable === '' ? undefined : photographable === 'yes',
      vertical: vertical.trim() || undefined,
      no_phone_reason: noPhoneReason.trim() || undefined,
      section_feedback: Object.fromEntries(
        Object.entries(feedback).filter(([, v]) => v.trim())
      ),
      run_id: crypto.randomUUID(),
      overrides: Object.keys(overrides).length
        ? Object.fromEntries(
            Object.entries(overrides).filter(([, v]) => v.trim())
          )
        : undefined,
      section_order: sectionOrder.length ? sectionOrder : undefined,
      // Carried purely so the saved draft can be grouped under this campaign
      // in history. It changes nothing about what is generated.
      campaign_resource_name: campaignRn || undefined,
      campaign_name:
        adCtx?.campaigns.find((c) => c.resource_name === campaignRn)?.name || undefined,
    };
    if (dshLaneOn()) dshRun.start(req);
    else preview.mutate(req);
  };

  // Escape closes the expanded view. A full-viewport overlay with no keyboard
  // exit is a trap for anyone not reaching for the mouse.
  useEffect(() => {
    if (!expanded) return;
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && setExpanded(false);
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [expanded]);

  // Why the page you are looking at has no photographs. Without this the
  // preview reads as a broken template rather than an empty client record —
  // the tool hides what it cannot source rather than inventing it, and that
  // choice is invisible unless the preview says so. The findings list already
  // carries the key; this puts the reason next to the evidence.
  //
  // ⚠ IT MATCHED THE CHECK FAMILY, NOT THE REASON, AND SAID SOMETHING FALSE.
  // `startsWith('LP-020')` fires on FOUR different findings and only one of
  // them means "no photo was used":
  //
  //   hero_unrelated_to_service   the photo does not show the service   ← this
  //   hero_needs_scrim            a photo IS used, text needs contrast
  //   hero_text_safety_unmeasured a photo IS used, contrast unknown
  //   portrait_hero_cropped       a photo IS used, wrong aspect
  //
  // A real agileandco draft carried hero_needs_scrim and portrait_hero_cropped
  // — both about a photograph sitting in the hero — and the banner told the
  // operator "the available photographs do not show this service, so none was
  // used" directly above the photograph that had been used. The page was right
  // and the sentence describing it was wrong, which is the same class of defect
  // as claiming a client has no phone number.
  //
  // Matching the REASON is what fixes it. A prefix wide enough to catch a
  // family will keep catching whatever is added to that family next.
  const imageryNote = useMemo(() => {
    const keys = [
      ...(qa?.fatal ?? []), ...(qa?.data_gaps ?? []),
      ...(qa?.suppressible ?? []), ...(qa?.warns ?? []),
    ];
    const has = (reason: string) =>
      keys.some((k) => k === reason || k.startsWith(`${reason}:`));

    if (has('BRD-03:no_imagery')) {
      return 'No usable photograph exists in this client’s pool, so the page renders without one. It is not a broken template — the tool leaves out what it cannot source rather than inventing it.';
    }
    if (has('BRD-04:not_public_url') || has('BRD-04:expiring_media_url')) {
      return 'A photograph was found but its URL is not publicly resolvable, so it was left out rather than shipped as a broken image.';
    }
    // ⚠ ORDER MATTERS: DECORATIVE IS ALSO UNRELATED. A decorative photo fires
    // hero_unrelated_to_service too — that is precisely why it was moved out of
    // the hero — so testing the unrelated case first announced "none was used"
    // above a photograph, the same contradiction #1770 fixed for a different
    // reason. The narrower fact has to be checked first.
    if (has('LP-020:hero_decorative_only')) {
      return 'No photograph shows this service, so the hero has none. One is used lower down as decoration only, with empty alt text — it illustrates the page without claiming to be the work.';
    }
    if (has('LP-020:hero_unrelated_to_service')) {
      return 'The available photographs do not show this service, so none was used. A bathroom photo on a kitchen page is worse than no photo.';
    }
    return null;
  }, [qa]);

  /* ⚠ THE PREVIEW WAS A LETTERBOX. See useFrameContentHeight: the frame was
   * pinned to the viewport and then the notice banners took their share, so a
   * 4,600px draft was reviewed through ~330px of window with its scrollbar
   * hidden on purpose. Inline, the frame now takes the page's own height and
   * the pane scrolls. Full screen keeps the fixed frame — there the window IS
   * the viewport, which is the case the internal scroll was designed for. */
  const shownHtml = layoutHtml ?? shown?.html;
  const previewDoc = useMemo(
    () => (shownHtml ? toPreviewDocument(shownHtml) : ''),
    [shownHtml],
  );
  const saveLayout = useMutation({
    mutationFn: (html: string) =>
      landingPagesService.putDraftHtml(tenantId, openDraftId as string, html),
    onSuccess: () => {
      setLayoutHtml(null);
      queryClient.invalidateQueries({ queryKey: ['landing-draft', tenantId, openDraftId] });
    },
  });
  const openInNewWindow = () => {
    const w = window.open('', '_blank');
    if (!w) return;
    w.document.open();
    w.document.write(previewDoc);
    w.document.close();
  };
  const inlineFrame = useFrameContentHeight(
    pane === 'page' && !!shown?.html && !expanded,
    previewDoc,
  );

  /* ⚠ DOWNLOADS THE RAW FRAGMENT, NOT `previewDoc`. `toPreviewDocument` swaps
   * every form shortcode for a dashed grey placeholder so the iframe (which
   * is not WordPress) doesn't render `[gravityform ...]` as literal text —
   * exactly the behaviour a downloaded file must NOT carry, or the lead form
   * silently becomes a static grey box the moment someone reuses this HTML
   * outside the builder. `shown.html` already carries its own inline
   * <style>/<script>, so wrapping it in a bare shell is the whole job. */
  const handleDownloadHtml = () => {
    if (!shown?.html) return;
    const doc = shown.html.includes('<html')
      ? shown.html
      : `<!DOCTYPE html>\n<html lang="en">\n<head>\n<meta charset="utf-8">\n` +
        `<meta name="viewport" content="width=device-width, initial-scale=1">\n` +
        `<title>${serviceName.trim() || 'Landing Page'}</title>\n</head>\n<body>\n${shown.html}\n</body>\n</html>\n`;
    const blob = new Blob([doc], { type: 'text/html' });
    const url = URL.createObjectURL(blob);
    const filename = `${tenantId || 'landing'}-${templateSlug || 'page'}.html`.replace(/[^a-z0-9._-]+/gi, '-');
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  /** The preview pane. One definition, rendered inline or full-viewport. */
  const paneBody = (full: boolean) => (
    <>
      <div className="flex shrink-0 flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <div className="inline-flex rounded-lg border border-border bg-card p-0.5">
            {(['shape', 'page'] as const).map((k) => {
              const disabled = k === 'page' ? !shown?.html : !selected?.shape;
              return (
                <button
                  key={k}
                  type="button"
                  disabled={disabled}
                  onClick={() => setPane(k)}
                  className={`rounded-md px-3 py-1.5 text-sm transition-colors disabled:opacity-40 ${
                    pane === k
                      ? 'bg-coral text-white'
                      : 'text-teal-dark hover:bg-teal-light'
                  }`}
                >
                  {k === 'shape'
                    ? 'Template shape'
                    : publishedId
                      ? 'Published page'
                      : 'Draft — not in WordPress'}
                </button>
              );
            })}
          </div>

          {/* Width applies to the built page only — the shape wireframe is a
              diagram, not a responsive document. */}
          {pane === 'page' && shown?.html && (
            <div className="inline-flex rounded-lg border border-border bg-card p-0.5">
              {([
                ['phone', 'Phone', Smartphone],
                ['desktop', 'Desktop', Monitor],
              ] as const).map(([k, label, Icon]) => (
                <button
                  key={k}
                  type="button"
                  onClick={() => setWidth(k)}
                  title={`${label} width`}
                  className={`inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-sm transition-colors ${
                    width === k
                      ? 'bg-coral text-white'
                      : 'text-teal-dark hover:bg-teal-light'
                  }`}
                >
                  <Icon className="h-4 w-4" />
                  {label}
                </button>
              ))}
            </div>
          )}
        </div>

        <div className="flex items-center gap-3">
          {shown?.html && !full && (
            <span className="text-xs text-muted-foreground">
              Draft only — nothing has been written to {tenantId}.
            </span>
          )}
          {shown?.html && (
            <button
              type="button"
              onClick={handleDownloadHtml}
              title="Download the generated page as a standalone HTML file"
              className="inline-flex items-center gap-1.5 rounded-lg border border-border
                         bg-card px-3 py-1.5 text-sm text-teal-dark hover:bg-teal-light"
            >
              <Download className="h-4 w-4" />
              Download HTML
            </button>
          )}
          <button
            type="button"
            onClick={() => setExpanded(!full)}
            className="inline-flex items-center gap-1.5 rounded-lg border border-border
                       bg-card px-3 py-1.5 text-sm text-teal-dark hover:bg-teal-light"
          >
            {full ? <Minimize2 className="h-4 w-4" /> : <Maximize2 className="h-4 w-4" />}
            {full ? 'Close (Esc)' : 'Full screen'}
          </button>
          {previewDoc && !full && (
            <button
              type="button"
              onClick={openInNewWindow}
              className="inline-flex items-center gap-1.5 rounded-lg border border-border
                         bg-card px-3 py-1.5 text-sm text-teal-dark hover:bg-teal-light"
            >
              <ExternalLink className="h-4 w-4" />
              New window
            </button>
          )}
        </div>
      </div>

      {viewingSaved && (
        <div className="mt-3 flex flex-wrap items-center justify-between gap-3 rounded border border-coral bg-coral-light px-3 py-2 text-sm">
          <span className="text-coral-dark">
            Viewing a saved draft from{' '}
            {new Date(openedDraft.data!.created_at).toLocaleString()}
            {openedDraft.data!.campaign_name
              ? ` · ${openedDraft.data!.campaign_name}`
              : ''}
            . The form has been set back to the inputs that produced it.
          </span>
          <button
            type="button"
            onClick={() => {
              void navigator.clipboard?.writeText(
                `${window.location.origin}${window.location.pathname}` +
                  `?draft=${openDraftId}&tenant=${encodeURIComponent(tenantId)}`,
              );
              setCopied(true);
              window.setTimeout(() => setCopied(false), 2000);
            }}
            className="rounded border border-border px-3 py-1 text-xs hover:bg-teal-light"
          >
            {copied ? 'Link copied' : 'Copy link to this run'}
          </button>
          <button
            type="button"
            onClick={() => {
              setOpenDraftId(null);
              setSearchParams(
                (prev) => {
                  const next = new URLSearchParams(prev);
                  next.delete('draft');
                  return next;
                },
                { replace: true },
              );
            }}
            className="rounded bg-coral px-3 py-1 text-xs text-white hover:bg-coral-hover"
          >
            {preview.data?.html ? 'Back to the latest run' : 'Close'}
          </button>
        </div>
      )}

      {/* ⚠ A STORED RUN'S FINDINGS BELONG TO THAT RUN. `qa` reads from `shown`,
          so opening a draft swaps the findings with the page rather than
          leaving one run's checks beside another run's HTML. */}
      {/* ⚠ THE PAGE LOOKS FINISHED EITHER WAY, AND THAT WAS THE PROBLEM. Every
          build returns `defaulted` — the values it could not source from the
          client's brand — the API has always sent it and nothing rendered it,
          so a wordmark standing in for a missing logo and a real logo looked
          identical here. Fallbacks are legitimate; silent ones are not. */}
      {pane === 'page' && shown?.html && brandGaps.length > 0 && (
        <div className="flex shrink-0 items-start gap-2 rounded-lg border border-border bg-card px-3 py-2 text-sm text-teal-dark">
          <Info className="mt-0.5 h-4 w-4 shrink-0 text-teal" />
          <span>
            Built with fallbacks for{' '}
            <span className="font-medium">{brandGaps.join(', ')}</span>. The page
            is complete; these are the values missing from this client&rsquo;s
            brand record.
          </span>
        </div>
      )}

      {pane === 'page' && shown?.html && imageryNote && (
        <div className="flex shrink-0 items-start gap-2 rounded-lg border border-border bg-card px-3 py-2 text-xs text-teal-dark">
          <ImageOff className="mt-0.5 h-4 w-4 shrink-0 text-teal" />
          <span>{imageryNote}</span>
        </div>
      )}

      {/* Expanded, the page runs edge to edge: no card padding, no border, no
          corner radius. Full screen that still frames the page in a grey
          gutter is not full screen. At desktop width those gutters also
          change where the page's own breakpoints land. */}
      {/* ⚠ TWO SCROLLBARS FOR ONE THING. This pane scrolled AND the iframe
          inside it scrolled its own 3,600px page, so a rendered draft showed
          a stack of vertical scrollbars and neither one obviously moved the
          page. When the iframe is what is on screen it owns the scrolling and
          this pane must not compete: the minimum height moves here so the
          frame can simply fill it. Everything else in this pane (the skeleton,
          the findings list) is ordinary content and still scrolls normally. */}
      <div
        className={`relative min-h-0 flex-1 ${
          pane === 'page' && shown?.html
            // ⚠ FULL SCREEN AND INLINE WANT OPPOSITE THINGS, AND TREATING THEM
            // ALIKE IS WHAT MADE THE INLINE PREVIEW A LETTERBOX.
            //
            // Full screen: the window IS the viewport, so the frame fills it
            // and the page scrolls inside — the case the hidden scrollbar and
            // the "no minimum height" note below were written for.
            //
            // Inline: the frame takes the PAGE's height and this pane scrolls
            // like any other content. Pinned to `calc(100vh - 13rem)` minus
            // two notice banners, a 4,600px draft was being reviewed through
            // roughly 330px of window with its scrollbar hidden on purpose —
            // which does not read as "scroll me", it reads as "the page is
            // cut" (Jacob, 2026-08-23).
            //
            // The original note still applies to the full-screen path: no
            // minimum height, because the column has an explicit one and a
            // 38rem floor forces the frame taller than its own column on any
            // viewport under ~810px.
            ? (full ? 'overflow-hidden' : 'overflow-visible')
            : 'overflow-y-auto'
        } ${
          full && pane === 'page'
            ? 'bg-white'
            : 'rounded-lg border border-border bg-muted/30 p-6'
        }`}
      >
        {pane === 'page' && shown?.html ? (
          <iframe
            title="Landing page draft"
            ref={full ? undefined : inlineFrame.ref}
            srcDoc={previewDoc}
            style={{
              ...(width === 'phone' ? { width: 390 } : null),
              // 0 until the first measurement lands; the class below keeps the
              // frame at full column height until then, so it never flashes
              // as a 0px strip.
              ...(full || !inlineFrame.height ? null : { height: inlineFrame.height }),
            }}
            className={`mx-auto bg-white ${
              full || !inlineFrame.height ? 'h-full' : ''
            } ${
              width === 'phone'
                ? `max-w-full ${full ? 'border-x border-border' : 'rounded shadow-sm'}`
                : 'w-full'
            } ${full ? '' : 'rounded'}`}
          />
        ) : engineChoosesLayout ? (
          <div className="flex h-full flex-col items-center justify-center gap-3 text-center">
            <LayoutTemplate className="h-10 w-10 text-muted-foreground/50" />
            <p className="text-sm font-medium">The engine chooses the layout</p>
            <p className="max-w-xs text-sm text-muted-foreground">
              It ideates three directions, picks one and builds it. There is no
              shape to show until the run finishes.
            </p>
          </div>
        ) : selected?.shape ? (
          <TemplateSkeleton
            shape={selected.shape}
            templateName={selected.name.replace(/^Landing\s*[—-]\s*/, '')}
          />
        ) : (
          <div className="flex h-full flex-col items-center justify-center gap-3 text-center">
            <LayoutTemplate className="h-10 w-10 text-muted-foreground/50" />
            <p className="text-sm font-medium">Pick a template</p>
            <p className="max-w-xs text-sm text-muted-foreground">
              Each one builds a different set of sections in a different order.
              You’ll see the shape here before generating anything.
            </p>
          </div>
        )}

        {/* Generating covers the pane rather than blanking it — the shape
            stays legible underneath, which is what you are waiting on. */}
        {generating && (
          <div className="absolute inset-0 flex items-center justify-center bg-background/70 backdrop-blur-[1px]">
            <div className="flex flex-col items-center gap-1 rounded-lg border border-border bg-card px-4 py-3 shadow-sm">
              <span className="flex items-center gap-2">
                <Loader2 className="h-4 w-4 animate-spin text-coral" />
                <span className="text-sm">Building the page…</span>
              </span>
              {dshRun.running && (
                <span className="text-xs text-muted-foreground">
                  {elapsedLabel} elapsed · typically about{' '}
                  {Math.round(DSH_MEDIAN_MS / 60_000)} minutes. Leaving this
                  screen does not stop the run; it lands in History either way.
                </span>
              )}
            </div>
          </div>
        )}
      </div>
    </>
  );

  if (!tenantId) {
    return (
      <div className="p-8 text-teal-dark">Select a client to build a page for.</div>
    );
  }

  return (
    // One provider for the whole screen: every field's info icon is a
    // Radix tooltip, and they need a shared provider to share delay and
    // dismissal. 200ms so it does not fire on a mouse passing through.
    // Two panes: what you decide on the left, what you get on the right. The
    // right pane is never empty once a template is picked — it shows the
    // shape that template builds, so choosing is not blind and does not cost
    // a generation to undo.
    // h-full is not usable here: the route's card parent is a block with a
    // min-height and auto height, so a percentage height has nothing to
    // resolve against and collapses to content. Match the parent's own
    // viewport-based measure instead. No padding — the card already has it.
    <TooltipProvider delayDuration={200}>
    <div className="mb-4 flex items-center gap-2 border-b border-border">
      {([
        ['builder', 'Builder'],
        ['history', 'History'],
        // ⚠ A TAB, NOT A SETTINGS PAGE. The person who can tell which form is
        // real is the person building the page, and they find out it matters
        // at the moment generation refuses. Anywhere else is a second trip.
        ['form', 'Lead form'],
        // ⚠ NOT TWEAKS. Rating and guarantee text license claims. The Tweaks
        // panel is written never to send those keys; this tab is the only
        // place an operator records them, with their name on the write.
        ['facts', 'Sourced facts'],
        ['favorites', 'Favorites'],
      ] as const).map(([k, label]) => (
        <button
          key={k}
          type="button"
          role="tab"
          aria-selected={tab === k}
          onClick={() => setTab(k)}
          /* ⚠ THE SELECTED TAB WAS UNREADABLE IN DARK MODE. It was
             `text-coral-dark`, whose dark value (#7BD9F5) is defined in the
             `.dark` block and was evidently not reaching this element —
             reported rendering as the LIGHT value #074E6F, a deep azure on a
             near-black strip, roughly 1.5:1 (Jacob, 2026-08-23).

             Fixed by removing the dependency rather than by chasing the
             token: `text-foreground` is readable in both themes by
             definition, and selection is still carried by the 2px
             `border-coral` underline and the weight change. A state that is
             only signalled by a colour is one token bug away from invisible
             anyway. Same reasoning for the unselected tabs, which were
             `text-teal-dark` (#374151) on black. */
          className={`-mb-px border-b-2 px-4 py-2 text-sm transition-colors ${
            tab === k
              ? 'border-coral font-medium text-foreground'
              : 'border-transparent text-muted-foreground hover:bg-accent hover:text-foreground'
          }`}
        >
          {label}
          {k === 'history' && (history.data?.total ?? 0) > 0 && (
            <span className="ml-2 rounded bg-muted px-1.5 py-0.5 text-xs tabular-nums text-muted-foreground">
              {history.data?.total}
            </span>
          )}
        </button>
      ))}
    </div>

    {/* ⚠ A LINK FOR ANOTHER CLIENT MUST SAY SO. The draft read is scoped by
        tenant server-side, so the wrong tenant returns nothing and the page
        renders empty — which looks exactly like a broken link, and sends the
        reviewer to ask us instead of switching client. Name the tenant the
        link was written for. */}
    {tenantMismatch && (
      <div className="mb-4 max-w-2xl rounded-md border-l-4 border-l-destructive bg-destructive/5 p-4">
        <p className="text-sm font-medium">This link is for a client you cannot open</p>
        <p className="mt-1 text-sm text-teal-dark">
          It opens a run belonging to <strong>{linkTenant}</strong>, which is not
          in your client list. You are currently viewing{' '}
          <strong>{tenantId || 'no client'}</strong>. Ask for access to{' '}
          {linkTenant}, then reopen the link.
        </p>
      </div>
    )}

    {tab === 'form' ? (
      <section className="min-h-[calc(100vh-16rem)]">
        <p className="mb-4 max-w-2xl text-sm text-muted-foreground">
          Which of {tenantId}&rsquo;s forms their landing pages embed. The
          generator picks this itself when their site runs exactly one form it
          recognises, and stops when there are several, because choosing wrong
          sends submissions to a notification nobody is watching.
        </p>
        <FormPicker tenantId={tenantId} />
      </section>
    ) : tab === 'facts' ? (
      <section className="min-h-[calc(100vh-16rem)]">
        <p className="mb-4 max-w-2xl text-sm text-muted-foreground">
          Google stars, guarantees and hours for {tenantId} — only when a
          named person records them. Empty stays empty. Tweaks cannot
          license these.
        </p>
        <ArtIntake tenantId={tenantId} />
      </section>
    ) : tab === 'favorites' ? (
      <section className="min-h-[calc(100vh-16rem)]">
        <p className="mb-4 max-w-2xl text-sm text-muted-foreground">
          Sections the team has kept, newest first. Shared with everyone —
          there are no private ones. Opening a favorite renders the section
          from the page revision it was taken from, not from that page as it
          stands today.
        </p>
        <div className="max-w-3xl">
          <FavoritesLibrary />
        </div>
      </section>
    ) : tab === 'history' ? (
      <section className="min-h-[calc(100vh-16rem)]">
        <p className="mb-4 max-w-2xl text-sm text-muted-foreground">
          Every page generated for {tenantId}, newest first. Grouped by the
          campaign it was built for, then by day. Opening one shows the page
          exactly as it was generated and puts the form back to the inputs
          that produced it.
        </p>
        <div className="max-w-2xl">
          <DraftHistory
            groups={history.data?.groups ?? []}
            total={history.data?.total ?? 0}
            isLoading={history.isLoading}
            isError={history.isError}
            openId={openDraftId}
            onOpen={openDraft}
          />
        </div>
      </section>
    ) : (
    <div className="flex min-h-[calc(100vh-13rem)] gap-6">
      <div className="flex w-[26rem] shrink-0 flex-col gap-4">
      {/* No <h1> here: the app chrome already renders one for this route.
          Two level-1 headings on a page is an a11y defect, and it was
          visible in the first ten seconds of actually opening the screen —
          which is the argument for opening it. */}
      {/* ⚠ THE SENTENCE IS THE FORM. Service, keyword, vertical, template,
          photographability and the supporting-keyword checklist were seven
          controls the operator had to read before pressing Generate. The
          template and the service are the two choices that shape the page,
          so they are the sentence's slots; keyword, vertical and photos are
          derived from the campaign or the client's record, stated on one line,
          and editable only behind "change", closed by default. */}
      <section className="rounded-lg border border-border bg-card p-4 text-sm">
        <div className="flex flex-wrap items-center gap-2">
          <span>Build a</span>
          <select
            aria-label="Template"
            className="min-w-0 max-w-full rounded border border-border bg-background px-2 py-1 text-foreground"
            value={templateSlug}
            onChange={(e) => {
              setTemplatePick(e.target.value);
              // Show what was just chosen. Picking a different template with
              // an old page still on screen is how you convince yourself the
              // choice did nothing.
              setPane('shape');
            }}
          >
            {(templates ?? []).map((t) => (
              <option key={t.slug} value={t.slug}>
                {t.name.replace(/^Landing\s*[—-]\s*/, '')}
              </option>
            ))}
          </select>
          {serviceOther || (candidates.length === 0
                            && (suggest?.services ?? []).length === 0) ? (
            <input
              aria-label="Service"
              className="min-w-[10rem] flex-1 rounded border border-border bg-background px-2 py-1 text-foreground"
              placeholder={(suggest?.services ?? []).length === 0
                ? 'No services on record — type one'
                : 'Type a service'}
              value={serviceName}
              onChange={(e) => setServiceName(e.target.value)}
            />
          ) : (
            <select
              aria-label="Service"
              className="min-w-0 max-w-full rounded border border-border bg-background px-2 py-1 text-foreground"
              value={knownService ? serviceName : ''}
              onChange={(e) => {
                const v = e.target.value;
                if (v === '__other__') { setServiceOther(true); setServiceName(''); return; }
                setServiceName(v);
                // Picking a campaign service settles the H1 too. Without this
                // the operator chooses "Residential" while the headline still
                // reads "fargo" — the shortest of 242 pooled keywords.
                const c = candidates.find((x) => x.name === v);
                if (c?.target_keyword) setKeyword(c.target_keyword);
              }}
            >
              <option value="">Choose a service…</option>
              {candidates.length > 0 && (
                <optgroup label="From this campaign">
                  {candidates.map((c) => (
                    <option key={c.name} value={c.name}>
                      {c.name} — {c.keyword_count} keyword
                      {c.keyword_count === 1 ? '' : 's'} in {c.ad_group}
                    </option>
                  ))}
                </optgroup>
              )}
              {(suggest?.services ?? []).length > 0 && (
                <optgroup label="From this client's record">
                  {(suggest?.services ?? []).map((v) => (
                    <option key={v} value={v}>{v}</option>
                  ))}
                </optgroup>
              )}
              <option value="__other__">
                {candidates.length > 0
                  ? 'None of these — type my own'
                  : 'Other — type it myself'}
              </option>
            </select>
          )}
          <span>page</span>
          {adCtx?.available && (
            <>
              <span>for</span>
              <select
                aria-label="Campaign"
                className="min-w-0 max-w-full rounded border border-border bg-background px-2 py-1 text-foreground"
                value={campaignRn}
                onChange={(e) => setCampaignRn(e.target.value)}
              >
                <option value="">Choose a campaign…</option>
                {adCtx.campaigns.map((c) => (
                  <option key={c.resource_name} value={c.resource_name}>
                    {c.name}
                    {c.status !== 'ENABLED' ? ` (${c.status.toLowerCase()})` : ''}
                  </option>
                ))}
              </select>
            </>
          )}
          <button
            type="button"
            disabled={!canGenerate || generating}
            onClick={run}
            className="inline-flex items-center gap-2 rounded bg-coral px-4 py-2 text-white
                       hover:bg-coral-hover disabled:opacity-50"
          >
            {generating ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Play className="h-4 w-4" />
            )}
            {generating ? 'Building…' : 'Build draft'}
          </button>
        </div>

        {selected?.description && (
          <p className="mt-2 text-xs text-muted-foreground">{selected.description}</p>
        )}
        <p className="mt-2 text-xs text-muted-foreground">
          Assumed: vertical {vertical.trim() || 'none'}
          {' · '}{keyword.trim() ? `keyword “${keyword.trim()}”` : 'no keyword'}
          {' · '}photos {photographable === '' ? 'strict' : photographable}
          {' · '}phone {noPhoneReason.trim() ? `none — ${noPhoneReason.trim()}` : 'from record'}
          {' '}
          <button
            type="button"
            aria-expanded={changeOpen}
            onClick={() => setChangeOpen((v) => !v)}
            className="text-coral underline-offset-2 hover:underline"
          >
            change
          </button>
        </p>

        {changeOpen && (
          <div className="mt-2 flex flex-wrap items-end gap-2 text-xs">
            <label className="flex flex-col gap-0.5">
              vertical
              <input
                className="w-32 rounded border border-border bg-background px-2 py-1 text-foreground"
                list="landing-known-verticals"
                value={vertical}
                onChange={(e) => setVertical(e.target.value)}
              />
              <datalist id="landing-known-verticals">
                {(suggest?.known_verticals ?? []).map((v) => (
                  <option key={v} value={v} />
                ))}
              </datalist>
            </label>
            <label className="flex flex-col gap-0.5">
              keyword
              <input
                className="w-44 rounded border border-border bg-background px-2 py-1 text-foreground"
                list="landing-researched-keywords"
                placeholder={suggest?.suggested_keyword ?? ''}
                value={keyword}
                onChange={(e) => setKeyword(e.target.value)}
              />
              <datalist id="landing-researched-keywords">
                {(suggest?.researched_keywords ?? []).map((k) => (
                  <option key={k.keyword} value={k.keyword} />
                ))}
              </datalist>
            </label>
            <label className="flex flex-col gap-0.5">
              photos
              {/* Tri-state on purpose: "nobody said" and "somebody said yes"
                  are different facts and the hero gate treats them
                  differently. */}
              <select
                className="rounded border border-border bg-background px-2 py-1 text-foreground"
                value={photographable}
                onChange={(e) => setPhotographable(e.target.value as '' | 'yes' | 'no')}
              >
                <option value="">Not stated — strict</option>
                <option value="yes">Yes — the work can be shown</option>
                <option value="no">No — nothing to photograph</option>
              </select>
            </label>
            <label className="flex flex-col gap-0.5">
              no-phone reason
              <input
                className="w-44 rounded border border-border bg-background px-2 py-1 text-foreground"
                placeholder="form-only page, no tracked line"
                value={noPhoneReason}
                onChange={(e) => setNoPhoneReason(e.target.value)}
              />
            </label>
          </div>
        )}
      </section>

      {/* ⚠ MOVED HERE FROM AFTER "Adjust this run", WHICH IS THE WHOLE BUG.
          A click on Generate settles in under ten seconds and nothing else on
          screen changes when it refuses — no page, no scroll. The result used
          to render below button text, hero sub-heading, phone override and a
          7-row section-reorder list, so on anything but a tall window the
          operator saw the button flip back to "Generate draft" with the
          result sitting off-screen below a settings panel they never opened.
          That reads as "nothing happened," not as a shown answer — reported
          twice as "the button doesn't do anything" for exactly this reason.
          Directly under the button now, so settling the mutation and seeing
          the result are the same moment. */}
      <div ref={resultRef}>
        {failure && (
          <section className="rounded-lg border-l-4 border-destructive bg-destructive/5 p-4">
            <h2 className="font-semibold">Generation failed</h2>
            <p className="mt-1 break-words text-sm text-teal-dark">{failure}</p>
            <p className="mt-2 text-sm text-teal-dark">
              This is a fault on our side, not a decision about the page —
              nothing was judged and nothing was written. Worth retrying; if it
              repeats, the backend log carries the upstream error.
            </p>
            <button
              type="button"
              disabled={!canGenerate || generating}
              onClick={run}
              className="mt-3 rounded border border-coral px-3 py-1.5 text-sm
                         text-coral hover:bg-coral hover:text-white disabled:opacity-50"
            >
              Try again
            </button>
          </section>
        )}

        {refusal && (
          <section className="rounded-lg border-l-4 border-coral bg-coral-light p-4">
            <h2 className="font-semibold">This page was not built</h2>
            <p className="mt-1 whitespace-pre-wrap text-sm text-teal-dark">{refusal}</p>
            <p className="mt-2 text-sm text-teal-dark">
              The tool declines rather than guessing. A form that posts nowhere
              looks fine and loses every lead.
            </p>
          </section>
        )}
      </div>

      {/* ⚠ AFTER THE TEMPLATE, BECAUSE IT DEPENDS ON ONE. A section order is a
          permutation of the chosen template's own sections, so there is
          nothing to reorder until one is picked, and the panel would be
          showing an empty list pretending to be a control. */}
      <section className="rounded-lg border border-border bg-card p-4">
        <h2 className="text-sm font-semibold">Keep a section</h2>
        <p className="mb-3 mt-1 text-xs text-muted-foreground">
          Saves one band of the page on screen to the team library, pinned to
          the revision it was taken from.
        </p>
        <SectionPicker
          html={shown?.html}
          copyMeta={shown?.copy_meta}
          draftId={publishedDraftId}
          viewingSaved={viewingSaved}
        />
      </section>

      {/* ⚠ REPAIR BELONGS BESIDE THE PAGE, NOT IN A TOOL OF ITS OWN. This was
          briefly its own route; a separate screen made an operator navigate
          away from the page they had just spotted the regression on, and split
          the surface in two. The slot list IS the page on screen. */}
      {shown?.html && tenantId && (
        <section className="rounded-lg border border-border bg-card p-4">
          <h2 className="text-sm font-semibold">Swap a band</h2>
          <p className="mb-3 mt-1 text-xs text-muted-foreground">
            Put back the band an earlier run of this client got right. One slot
            at a time, and only this client's own runs.
          </p>
          <BandSwapPanel
            key={`${openDraftId ?? 'latest'}:${shown.html.length}`}
            tenantId={tenantId}
            pageHtml={shown.html}
            copyMeta={shown.copy_meta}
            currentDraftId={openDraftId}
          />
        </section>
      )}

      {viewingSaved && shown?.html && (
        <DraftLayout
          key={openDraftId as string}
          html={shown.html}
          onChange={setLayoutHtml}
          onSave={saveLayout.mutate}
          saving={saveLayout.isPending}
        />
      )}

      {selected?.shape?.sections?.length ? (
        <section className="rounded-lg border border-border bg-card p-4">
          <h2 className="text-sm font-semibold">Adjust this run</h2>
          <p className="mb-3 mt-1 text-xs text-muted-foreground">
            Applies to the next draft you generate and is saved with it, so
            reopening the run brings these back.
          </p>
          <BuilderTweaks
            overrides={overrides}
            onOverride={(k, v) => setOverrides((o) => ({ ...o, [k]: v }))}
            templateSections={selected.shape.sections}
            sectionOrder={sectionOrder}
            onSectionOrder={setSectionOrder}
          />
        </section>
      ) : null}

      {qa && (
          <aside className="flex flex-col gap-4">
            {/* The header count is the highest-value line on the panel:
                "2 you can clear now" is a decision, "6 findings" is a number.
                Viktor's framing, and it is the difference between a queue
                someone works and a list someone scrolls past. */}
            <div
              className={`rounded-lg border p-4 ${
                qa.passed
                  ? 'border-border bg-card'
                  : 'border-destructive/30 bg-destructive/5'
              }`}
            >
              <p className="font-semibold">
                {qa.passed ? 'Ready to publish' : 'Held'}
              </p>
              <p className="mt-1 text-sm text-teal-dark">
                {counts.you > 0 && (
                  <span className="font-medium text-coral">
                    {counts.you} you can clear now
                  </span>
                )}
                {counts.you > 0 && counts.someone > 0 && ' · '}
                {counts.someone > 0 && <span>{counts.someone} waiting on someone else</span>}
                {(counts.you > 0 || counts.someone > 0) && counts.info > 0 && ' · '}
                {counts.info > 0 && <span>{counts.info} worth knowing</span>}
                {routed.length === 0 && 'Nothing outstanding.'}
              </p>
              {/* Plain English by default. The check IDs are how the people
                  who wrote the gate talk about it, and they are meaningless
                  to an account manager reading a client's page — but they are
                  what you quote in a bug report, so they stay one click
                  away rather than being deleted. */}
              <button
                type="button"
                onClick={() => setShowKeys(!showKeys)}
                className="mt-2 text-xs text-teal-dark underline underline-offset-2 hover:text-coral"
              >
                {showKeys ? 'Hide check IDs' : 'Show check IDs'}
              </button>
              <p className="mt-2 text-xs text-teal">
                Form: {shown?.form_provider} #{shown?.form_id}
                {shown?.form_source === 'detected' && (
                  <span className="text-coral-dark">
                    {' '}— detected from their site, not confirmed
                  </span>
                )}
              </p>
            </div>

            {GROUPS.map(({ owner, label, tone }) => {
              const items = routed.filter((f) => f.owner === owner);
              if (!items.length) return null;
              return (
                <div key={owner} className="rounded-lg border border-border bg-card p-4">
                  <h3 className={`font-semibold ${tone}`}>
                    {label} ({items.length})
                  </h3>
                  <ul className="mt-3 space-y-4">
                    {items.map((f) => (
                      <li key={f.key} className="text-sm">
                        <p className="flex items-start gap-2">
                          {/* Blocking and shipped-anyway differ in KIND, not
                              just colour — a warning that shipped is not the
                              same event as a check that stopped the page. */}
                          {f.blocking ? (
                            <Ban className="mt-0.5 h-4 w-4 shrink-0 text-destructive" />
                          ) : (
                            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-teal" />
                          )}
                          <span className={f.says ? 'font-medium' : 'break-all font-mono text-xs'}>
                            {f.says ?? f.key}
                          </span>
                        </p>
                        {f.fix && <p className="ml-6 mt-1 text-teal-dark">{f.fix}</p>}
                        {f.who && (
                          <p className="ml-6 mt-1 text-xs text-coral-dark">
                            Needs {f.who}
                          </p>
                        )}
                        {/* Only when the sentence above is NOT already the key. */}
                        {f.says && showKeys && (
                          <code className="ml-6 mt-1 block break-all text-xs text-teal">
                            {f.key}
                          </code>
                        )}
                        {/* The one finding an operator could not act on from
                            here. "No usable photo exists" is unarguable and
                            was a dead end; the photographs usually exist and
                            the gap was only that adding them meant leaving
                            this screen. */}
                        {/^(BRD-03|LP-020)/.test(f.key) && (
                          <div className="ml-6">
                            {/* ⚠ NOT JUST THE UPLOAD BOX ANY MORE. The finding
                                says "no usable photo"; what it never said was
                                the priority Chans asked us to state, or what
                                the generator had already tried on its own —
                                stock and AI can both have run and been rejected
                                with the page reporting nothing but silence. */}
                            <HeroImagery
                              tenantId={tenantId}
                              serviceName={serviceName}
                              photographable={
                                photographable === ''
                                  ? null
                                  : photographable === 'yes'
                              }
                              plan={
                                shown && 'media_plan' in shown ? shown.media_plan : undefined
                              }
                              onUploaded={() => {
                                // The pool is what the next generation reads,
                                // so drop the cached dossier or a regenerate
                                // would look at the library from before the
                                // upload and refuse for the same reason.
                                queryClient.invalidateQueries({
                                  queryKey: ['landingPages', 'suggestions'],
                                });
                              }}
                            />
                          </div>
                        )}
                      </li>
                    ))}
                  </ul>
                </div>
              );
            })}

            {/* Revision notes, section by section. The sections come from the
                chosen template's own shape, so you can only leave a note on
                something the page actually renders — a note on a section this
                treatment does not build is a note nobody will ever action. */}
            {selected?.shape && (
              <div className="rounded-lg border border-border bg-card p-4">
                <h3 className="flex items-center gap-2 font-semibold">
                  <MessageSquare className="h-4 w-4 text-teal" />
                  Notes for the next draft
                  {noteCount > 0 && (
                    <span className="rounded-full bg-coral px-2 py-0.5 text-xs text-white">
                      {noteCount}
                    </span>
                  )}
                </h3>
                <p className="mt-1 text-xs text-teal-dark">
                  What to change, section by section. These steer wording and
                  emphasis — they cannot license a claim the client’s record
                  does not support, and every check still has to pass.
                </p>

                <div className="mt-3 space-y-3">
                  {['hero', ...selected.shape.sections].map((section) => (
                    <label key={section} className="block">
                      {/* No `capitalize`: SECTION_LABEL is already cased, and
                          the class title-cased it into "Hero And Headline". */}
                      <span className="text-xs font-medium">
                        {SECTION_LABEL[section] ?? section}
                      </span>
                      <textarea
                        rows={2}
                        value={feedback[section] ?? ''}
                        onChange={(e) => noteFor(section, e.target.value)}
                        placeholder={SECTION_PLACEHOLDER[section] ?? 'What should change here?'}
                        className="mt-1 w-full resize-y rounded border border-border bg-background
                                   px-2 py-1.5 text-sm text-foreground placeholder:text-muted-foreground"
                      />
                    </label>
                  ))}
                </div>

                <div className="mt-3 flex items-center gap-2">
                  <button
                    type="button"
                    disabled={!canGenerate || generating}
                    onClick={run}
                    className="rounded bg-coral px-3 py-1.5 text-sm text-white
                               hover:bg-coral-hover disabled:opacity-50"
                  >
                    {noteCount ? `Regenerate with ${noteCount} note${noteCount === 1 ? '' : 's'}` : 'Regenerate'}
                  </button>
                  {noteCount > 0 && (
                    <button
                      type="button"
                      onClick={() => {
                        setFeedback({});
                        try {
                          localStorage.removeItem(fbKey);
                        } catch { /* nothing to clear */ }
                      }}
                      className="text-sm text-teal-dark hover:text-coral"
                    >
                      Clear
                    </button>
                  )}
                </div>
              </div>
            )}

            {/* A manifest, not a confirm modal. Viktor: a hard confirm on
                every publish trains the muscle memory of clicking through,
                and then the one publish that should have stopped her looks
                exactly like the ninety that did not. Loud-always is
                quiet-always with extra clicks. What actually protects the
                client is stating precisely what is about to be written —
                and the two things wrong all week were the form and the
                phone, both of which are on this list. */}
            {qa.passed && (
              <div className="rounded-lg border border-coral/40 bg-coral-light p-4">
                <h3 className="font-semibold">About to write</h3>
                <dl className="mt-2 space-y-1 text-sm">
                  <div className="flex justify-between gap-3">
                    <dt className="text-teal-dark">Site</dt>
                    <dd className="text-right font-medium">{tenantId}</dd>
                  </div>
                  <div className="flex justify-between gap-3">
                    <dt className="text-teal-dark">Status</dt>
                    <dd className="text-right font-medium">noindex draft</dd>
                  </div>
                  <div className="flex justify-between gap-3">
                    <dt className="text-teal-dark">Form</dt>
                    <dd className="text-right font-medium">
                      {shown?.form_provider} #{shown?.form_id}
                    </dd>
                  </div>
                  <div className="flex justify-between gap-3">
                    <dt className="text-teal-dark">Phone that renders</dt>
                    <dd className="text-right font-medium">
                      {(shown?.html ?? '').includes('href="tel:')
                        ? ((shown?.html ?? '').match(/href="tel:([^"]+)"/)?.[1] ?? '—')
                        : `none${noPhoneReason ? ` — ${noPhoneReason}` : ''}`}
                    </dd>
                  </div>
                </dl>
                {counts.info + counts.someone > 0 && (
                  <p className="mt-2 text-xs text-coral-dark">
                    Publishing with {counts.someone + counts.info} unresolved:
                    listed above, not counted away.
                  </p>
                )}
                {/* The manifest above IS the confirmation. Viktor: a hard
                    confirm on every publish trains clicking through, and then
                    the one that should have stopped you looks like the ninety
                    that shouldn't. What protects the client is stating exactly
                    what is about to be written, which the list does. */}
                <button
                  type="button"
                  disabled={publish.isPending || !!published}
                  onClick={() =>
                    publish.mutate({
                      // ⚠ The run being published. Without it the backend has
                      // no row to record the WordPress post id against, and
                      // History keeps showing the page as never published.
                      draft_id: publishedDraftId,
                      artifact_id: publishedArtifactId,
                      tenant_id: tenantId,
                      template_slug: templateSlug,
                      service_name: serviceName.trim(),
                      target_keyword: keyword.trim() || undefined,
                      service_photographable:
                        photographable === ''
                          ? undefined
                          : photographable === 'yes',
                      vertical: vertical.trim() || undefined,
                      no_phone_reason: noPhoneReason.trim() || undefined,
                      section_feedback: Object.fromEntries(
                        Object.entries(feedback).filter(([, v]) => v.trim())
                      ),
                      run_id: crypto.randomUUID(),
                    })
                  }
                  className="mt-3 inline-flex w-full items-center justify-center gap-2
                             rounded bg-coral px-4 py-2 text-white hover:bg-coral-hover
                             disabled:opacity-50"
                >
                  {publish.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
                  {publish.isPending
                    ? 'Publishing…'
                    : published
                      ? 'Published'
                      : 'Publish as noindex draft'}
                </button>

                {publishError && (
                  <p className="mt-2 text-xs text-destructive">
                    Not published: {publishError}
                  </p>
                )}

                {published && (
                  <div className="mt-2 rounded border border-coral/40 bg-background p-2 text-xs">
                    <p className="font-medium">
                      Draft created on {tenantId}
                      {publishedId ? ` (post ${publishedId})` : ''}
                    </p>
                    {publishedUrl && (
                      <a
                        href={publishedUrl}
                        target="_blank"
                        rel="noreferrer"
                        className="mt-1 inline-flex items-center gap-1 text-coral underline"
                      >
                        Open in WordPress
                        <ExternalLink className="h-3 w-3" />
                      </a>
                    )}
                    <p className="mt-1 text-teal-dark">
                      It is a noindex draft and is not live yet.
                    </p>
                  </div>
                )}

                <p className="mt-2 text-xs text-teal-dark">
                  A human flips it live in wp-admin. Core cannot see that
                  moment — the test-lead gate does not reach it.
                </p>
              </div>
            )}

            {(qa.fatal ?? []).some((f) => f.startsWith('LC-07') || f.startsWith('LC-09')) && (
              <div className="rounded-lg border border-coral/40 bg-coral-light p-4">
                <h3 className="font-semibold">Ship without a phone?</h3>
                <p className="mt-1 text-xs text-teal-dark">
                  Only for this page, and only with a reason. It is stamped on
                  the page and shown here every time — never saved to the client.
                </p>
                <input
                  className="mt-2 w-full rounded border border-border bg-background px-3 py-2 text-sm text-foreground"
                  placeholder="e.g. form-only page, client has no tracked line"
                  value={noPhoneReason}
                  onChange={(e) => setNoPhoneReason(e.target.value)}
                />
                <button
                  type="button"
                  disabled={!noPhoneReason.trim() || generating}
                  onClick={run}
                  className="mt-2 rounded border border-coral px-3 py-1.5 text-sm
                             text-coral hover:bg-coral hover:text-white disabled:opacity-50"
                >
                  Regenerate with this reason
                </button>
              </div>
            )}
          </aside>
      )}
      </div>

      {/* RIGHT PANE — always showing something. Rendered inline, or over the
          whole viewport when expanded; same markup either way so the two
          cannot drift. */}
      {/* Only ONE copy is ever mounted. Rendering both put two iframes of the
          same page in the DOM at once — the landing page carries a click-ID
          script, so a hidden second copy is a second execution of it, plus
          duplicated ids for anything inside. */}
      {/* ⚠ THE PANE MUST NOT STRETCH TO THE FORM COLUMN. These are flex
          siblings, so the row is as tall as the TALLER of them — and once a
          draft exists the left column carries the findings list plus eight
          revision textareas, which is routinely taller than the generated page.
          The frame inherited that height through `h-full`, and the document
          ended long before the frame did, leaving a tall band of `bg-white`
          under the last section. Jacob read it, correctly, as an empty section
          in the page: "what is the empty white section below the enhance your
          digital strategy".

          Measured on the live app: parent 2939px, frame 2889px, and at desktop
          width the page itself is ~3135px — so the gap grows with the form,
          not with the draft. The page has no trailing whitespace at all
          (gapAfterFinal: 0), which is why it could never be fixed in the CSS.

          Bounding the pane to the viewport fixes it and is better anyway: the
          preview now stays in place while the form scrolls, instead of sliding
          away exactly when you are editing the thing it previews. */}
      {/* ⚠ THE COLUMN IS ONLY PINNED WHEN THERE IS NOTHING TO SCROLL. With a
          page rendered it takes the page's height and scrolls with the
          document, so the whole draft can be read; the fixed viewport height
          is what turned a 4,600px page into a ~330px letterbox. Without a page
          (the template skeleton, the empty state) the old behaviour is right:
          pinned beside the form so it does not slide away while you type. */}
      <div
        className={`flex min-h-0 min-w-0 flex-1 flex-col gap-3 self-start ${
          pane === 'page' && shown?.html
            ? ''
            : 'sticky top-4 h-[calc(100vh-13rem)]'
        }`}
      >
        {expanded ? (
          <div className="flex h-full items-center justify-center rounded-lg border border-dashed border-border text-sm text-muted-foreground">
            Showing full screen
          </div>
        ) : (
          paneBody(false)
        )}
      </div>

      {expanded && (
        <div className="fixed inset-0 z-50 flex flex-col gap-2 bg-background p-2">
          {paneBody(true)}
        </div>
      )}
    </div>
    )}
    </TooltipProvider>
  );
}
